import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Button, message, Row, Col, Upload, Modal } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import API from "../../api";

const BranchForm = ({ open, setOpen, initialValue, isEdit, onSubmit }) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(initialValue?.logo || null);

  // Populate the form when branch changes
  useEffect(() => {
    if (open) {
      if (isEdit && initialValue) {
        // Set form values for edit mode
        form.setFieldsValue({
          ...initialValue,
        });
        setLogoPreview(initialValue.logo);
      } else {
        form.resetFields(); // Reset form fields for add mode
        setLogoPreview(null);
      }
    }
  }, [isEdit, initialValue, open, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        formData.append(key, value);
      });
      if (logoFile) {
        formData.append("logo", logoFile);
      }
      await onSubmit(formData);
      form.resetFields();
      setLogoFile(null);
      setLogoPreview(null);
      setOpen(false);
    } catch (error) {
      console.error("Submit failed:", error);
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const showConfirm = (isEdit, onOk) => {
    Modal.confirm({
      title: isEdit ? "Update Branch" : "Save Branch",
      content: `Are you sure you want to ${isEdit ? "update" : "save"} this branch?`,
      okText: isEdit ? "Update" : "Save",
      cancelText: "Cancel",
      onOk,
    });
  };

  const handleFormFinish = (values) => {
    showConfirm(isEdit, () => onFinish(values));
  };

  // Validation patterns
  const phonePattern = /^(\+91\s?)?[6-9]\d{9}$/;

  return (
    <Drawer
      title={isEdit ? "Edit Branch" : "Add Branch"}
      width={800}
      placement="right"
      onClose={() => setOpen(false)}
      open={open}
    >
      <Form form={form} layout="vertical" onFinish={handleFormFinish}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Branch Name"
              name="name"
              rules={[{ required: true, message: "Please enter the branch name!" }]}
            >
              <Input placeholder="Enter Branch Name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Billing Header Name"
              name="billingHeaderName"
            >
              <Input placeholder="Enter Billing Header Name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Billing Prefix"
              name="billingPrefix"
              rules={[
                { required: true, message: "Please enter the billing prefix!" },
                {
                  pattern: /^[A-Z0-9]{1,5}$/,
                  message: "Prefix should be 1-5 characters (letters/numbers only)",
                },
              ]}
            >
              <Input placeholder="e.g., R3, T1, M2" maxLength={5} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Phone"
              name="phone"
              rules={[
                { required: true, message: "Please enter the phone number!" },
                {
                  pattern: phonePattern,
                  message: "Please enter a valid 10-digit phone number (e.g., +91 9707495702 or 9707495702)",
                },
              ]}
            >
              <Input placeholder="e.g., +91 9707495702 or 9707495702" maxLength={14} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { type: 'email', message: 'Please enter a valid email address!' },
              ]}
            >
              <Input placeholder="e.g., info@company.com" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Logo (optional)">
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={file => {
              setLogoFile(file);
              const reader = new FileReader();
              reader.onload = e => setLogoPreview(e.target.result);
              reader.readAsDataURL(file);
              return false; // Prevent auto upload
            }}
            maxCount={1}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="logo" style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 8, border: '1px solid #eee' }} />
            ) : (
              <Button icon={<PlusOutlined />}>Upload Logo</Button>
            )}
          </Upload>
          {isEdit && initialValue?.logo && !logoPreview && (
            <img src={initialValue.logo} alt="logo" style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 8, border: '1px solid #eee', marginTop: 8 }} />
          )}
        </Form.Item>

        <Form.Item
          label="Address"
          name="address"
          rules={[{ required: true, message: "Please enter the address!" }]}
        >
          <Input.TextArea rows={3} placeholder="Please enter the address" />
        </Form.Item>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="State"
              name="state"
              rules={[{ required: true, message: "Please enter the State!" }]}
            >
              <Input placeholder="Please enter the State" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="State Code"
              name="stateCode"
            >
              <Input placeholder="Please enter the State Code" />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="PAN"
              name="pan"
            >
              <Input placeholder="Please enter the PAN" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="GST Number"
              name="gstNo"
            >
              <Input placeholder="Please enter the GST Number" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={isLoading} block>
            {isEdit ? "Update Branch" : "Save Branch"}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default BranchForm;
